import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { calculateDailyBaselines } from '@vitalai/baseline-engine';
import { generateDemoHistory } from '@vitalai/health-normalizer';
import type { DailyBaselines, DailyScores, DetectedTrend, MealItem, NormalizedDailyMetrics } from '@vitalai/health-models';
import type { MealLogInput } from '@vitalai/nutrition-core';
import { calculateDailyScores } from '@vitalai/scoring-engine';
import { detectTrends } from '@vitalai/trend-engine';
import { checkApiStatus, fetchHistory, fetchToday, getApiBaseUrl, logMeal as apiLogMeal, syncDays } from './apiClient';
import { useDataSourceMode, type DataSourceMode } from './useDataSourceMode';

interface HealthDataContextValue {
  history: NormalizedDailyMetrics[];
  historyByDate: Map<string, NormalizedDailyMetrics>;
  todayKey: string;
  today: NormalizedDailyMetrics;
  scores: DailyScores;
  baselines: DailyBaselines;
  trends: DetectedTrend[];
  /** De donde vinieron estos datos. Puede ser 'demo' aunque el usuario haya
   *  elegido 'cloudflare' en Perfil, si el backend no esta disponible. */
  mode: DataSourceMode;
  /** Mensaje explicando por que se cayo a datos demo, o null si todo esta bien. */
  backendError: string | null;
  lastSyncAt: string | null;
  /** Genera 90 dias de datos demo nuevos y los sincroniza al backend. Solo tiene efecto en modo 'cloudflare'. */
  resyncDemoData: () => Promise<void>;
  /**
   * Registra una comida. En modo 'cloudflare' persiste en D1 via
   * POST /api/nutrition/meals y recarga today/history/scores reales. En modo
   * 'demo' actualiza el estado local nada mas (no hay backend al que
   * escribir), recalculando scores/baselines/trends con los engines del
   * Sprint 1.
   */
  logMeal: (input: MealLogInput) => Promise<void>;
}

type LoadState = { status: 'loading' } | { status: 'ready'; value: HealthDataContextValue };

type LocalComputation = Pick<
  HealthDataContextValue,
  'history' | 'historyByDate' | 'todayKey' | 'today' | 'scores' | 'baselines' | 'trends'
>;

function computeLocally(history: NormalizedDailyMetrics[]): LocalComputation {
  const lastDay = history[history.length - 1];
  if (!lastDay) {
    throw new Error('No hay historico para calcular baseline/scores/trends');
  }
  const todayKey = lastDay.date;
  const baselines = calculateDailyBaselines(history, todayKey);
  const scores = calculateDailyScores(history, todayKey);
  const trends = detectTrends(history, todayKey);
  const historyByDate = new Map(history.map((day) => [day.date, day]));
  return { history, historyByDate, todayKey, today: lastDay, scores, baselines, trends };
}

function buildDemoValue(): LocalComputation {
  return computeLocally(generateDemoHistory({ days: 90 }));
}

/** Combina los items de un MealLogInput en un unico MealItem (nuestro modelo cliente es un alimento por comida). */
function buildMealItemFromInput(input: MealLogInput): MealItem {
  const first = input.items[0];
  const sum = (selector: (item: (typeof input.items)[number]) => number) =>
    input.items.reduce((total, item) => total + selector(item), 0);

  return {
    id: crypto.randomUUID(),
    name: first?.name ?? 'Comida',
    mealType: input.mealType,
    time: input.time,
    grams: first?.grams,
    calories: sum((item) => item.calories),
    protein: sum((item) => item.protein),
    carbs: sum((item) => item.carbs),
    fat: sum((item) => item.fat),
    fiber: sum((item) => item.fiber),
    sodium: sum((item) => item.sodium),
    sugar: sum((item) => item.sugar),
    confidence: first?.confidence,
    photoKey: input.photoKey,
  };
}

/** Aplica una comida nueva al historico local (modo demo): agrega el item y re-suma los totales del dia. */
function applyMealToHistory(history: NormalizedDailyMetrics[], date: string, meal: MealItem): NormalizedDailyMetrics[] {
  return history.map((day) => {
    if (day.date !== date) return day;
    const meals = [...day.nutrition.meals, meal];
    return {
      ...day,
      nutrition: {
        calories: meals.reduce((sum, item) => sum + item.calories, 0),
        protein: meals.reduce((sum, item) => sum + item.protein, 0),
        carbs: meals.reduce((sum, item) => sum + item.carbs, 0),
        fat: meals.reduce((sum, item) => sum + item.fat, 0),
        fiber: meals.reduce((sum, item) => sum + item.fiber, 0),
        sodium: meals.reduce((sum, item) => sum + item.sodium, 0),
        sugar: meals.reduce((sum, item) => sum + item.sugar, 0),
        meals,
      },
    };
  });
}

const HealthDataContext = createContext<HealthDataContextValue | null>(null);

export function HealthDataProvider({ children }: { children: ReactNode }) {
  const [mode] = useDataSourceMode();
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  const logMeal = useCallback(
    async (input: MealLogInput): Promise<void> => {
      if (mode === 'cloudflare') {
        await apiLogMeal(input);
        // Mas simple y consistente que actualizar el estado a mano: recargar
        // desde el backend, la misma fuente de verdad que ya recalculo el
        // score al procesar la comida.
        setReloadToken((token) => token + 1);
        return;
      }

      const meal = buildMealItemFromInput(input);
      setState((prev) => {
        if (prev.status !== 'ready') return prev;
        const updatedHistory = applyMealToHistory(prev.value.history, input.date, meal);
        const recomputed = computeLocally(updatedHistory);
        return {
          status: 'ready',
          value: { ...recomputed, mode: 'demo', backendError: null, lastSyncAt: null, resyncDemoData: prev.value.resyncDemoData, logMeal: prev.value.logMeal },
        };
      });
    },
    [mode],
  );

  useEffect(() => {
    let cancelled = false;
    // React (StrictMode en particular) puede invocar este efecto dos veces
    // seguidas en desarrollo; sin abortar la corrida anterior, ambas
    // llamadas de red quedan en vuelo a la vez y la que responde ultima
    // "gana" aunque sea la obsoleta. Abortamos explicitamente en el cleanup.
    const controller = new AbortController();
    setState({ status: 'loading' });

    function pushDemoDataAndReload(): Promise<void> {
      return syncDays(generateDemoHistory({ days: 90 }), 'demo').then(() => {
        setReloadToken((token) => token + 1);
      });
    }

    async function run() {
      if (mode === 'demo') {
        const local = buildDemoValue();
        if (!cancelled) {
          setState({
            status: 'ready',
            value: { ...local, mode: 'demo', backendError: null, lastSyncAt: null, resyncDemoData: pushDemoDataAndReload, logMeal },
          });
        }
        return;
      }

      // mode === 'cloudflare'
      const isUp = await checkApiStatus(controller.signal);
      if (cancelled) return;
      if (!isUp) {
        const local = buildDemoValue();
        setState({
          status: 'ready',
          value: {
            ...local,
            mode: 'demo',
            backendError: `No se pudo conectar con ${getApiBaseUrl()}. Mostrando datos demo locales.`,
            lastSyncAt: null,
            resyncDemoData: pushDemoDataAndReload,
            logMeal,
          },
        });
        return;
      }

      try {
        let today = await fetchToday(controller.signal);
        if (!cancelled && (!today?.metrics || !today.scores)) {
          // Sin datos todavia (o un sync anterior quedo a medias, sin scores
          // calculados): sembramos 90 dias de demo data para poder demostrar
          // el pipeline real (Worker + D1) sin esperar a tener HealthKit
          // conectado en un dispositivo real (Sprint 2).
          await syncDays(generateDemoHistory({ days: 90 }), 'demo', controller.signal);
          today = await fetchToday(controller.signal);
        }
        if (cancelled) return;
        if (!today?.metrics || !today.scores) {
          throw new Error('El backend no devolvio datos despues de sincronizar.');
        }

        const history = await fetchHistory(90, controller.signal);
        if (cancelled) return;
        const historyByDate = new Map(history.map((day) => [day.date, day]));

        setState({
          status: 'ready',
          value: {
            history,
            historyByDate,
            todayKey: today.date,
            today: today.metrics,
            scores: today.scores,
            baselines: today.baselines,
            trends: today.trends,
            mode: 'cloudflare',
            backendError: null,
            lastSyncAt: today.lastSyncAt,
            resyncDemoData: pushDemoDataAndReload,
            logMeal,
          },
        });
      } catch (error) {
        if (cancelled) return;
        const local = buildDemoValue();
        setState({
          status: 'ready',
          value: {
            ...local,
            mode: 'demo',
            backendError: error instanceof Error ? error.message : String(error),
            lastSyncAt: null,
            resyncDemoData: pushDemoDataAndReload,
            logMeal,
          },
        });
      }
    }

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [mode, reloadToken, logMeal]);

  if (state.status === 'loading') {
    return (
      <div className="loading-screen">
        <p>Cargando VitalAI…</p>
      </div>
    );
  }

  return <HealthDataContext.Provider value={state.value}>{children}</HealthDataContext.Provider>;
}

export function useHealthData(): HealthDataContextValue {
  const context = useContext(HealthDataContext);
  if (!context) {
    throw new Error('useHealthData debe usarse dentro de <HealthDataProvider>');
  }
  return context;
}
